<template>
  <v-col cols="12">
    <!-- download function -->
    <div class="my-3"
         v-if="selected.length > 0">
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="success"
                 dark
                 v-bind="attrs"
                 v-on="on"
                 rounded
                 small
                 class="ml-2">
            <span class="font-weight-bold">Download</span>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item>
            <v-list-item-title>
              <json-excel type="xls"
                          :data="selected"
                          name="Computationally_predicted_AS_events.xls">
                <v-btn text
                       block> <span class="font-weight-bold">xls</span> </v-btn>
              </json-excel>
            </v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>
              <json-excel type="csv"
                          :data="selected"
                          name="Computationally_predicted_AS_events.csv">
                <v-btn text
                       block> <span class="font-weight-bold">csv</span> </v-btn>
              </json-excel>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-expansion-panels class="data-table-style">
      <v-expansion-panel>
        <v-expansion-panel-header expand-icon="mdi-menu-down"
                                  style="padding: 10px 25px">
          <div class="d-flex font-weight-bold">
            <span class="blue--text font-weight-bold mt-1">Display Columns</span>
            <v-chip outlined
                    small
                    color="success"
                    class="ml-2"
                    @click="showAll()">
              Select All
            </v-chip>
            <v-chip outlined
                    small
                    color="error"
                    class="ml-2"
                    @click="showDefault()">
              Select Default
            </v-chip>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="v-item-group">
            <v-checkbox v-for="(header, index) in headers"
                        :key="index"
                        :label="header.text"
                        :value="header.text"
                        v-model="selectedHeader"
                        dense
                        class="mx-1"></v-checkbox>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-data-table dense
                  class="data-table-style mt-1"
                  :headers="selectedHeaders"
                  fixed-header
                  :items="splice_datasets"
                  v-model="selected"
                  item-key="_id"
                  :search="search"
                  show-select
                  :single-select="singleSelect"
                  :loading="loading"
                  loading-text="Loading... Please wait"
                  :page.sync="currentPage"
                  :items-per-page="pageSize"
                  hide-default-footer
                  v-show="selectedHeaders && selectedHeaders.length > 0">
      <!-- item.event_id slot -->
      <template v-slot:[`item.event_id`]="{item}">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs"
                  v-on="on">{{item.event_id}}</span>
          </template>
          <span><span class="error--text font-weight-bold">Event ID: </span> Gene _ Chromsome _ Strand _ AS Type _ Event Coordinates</span>
        </v-tooltip>
      </template>
      <!-- item.as_model_id slot -->
      <template v-slot:[`item.as_model_id`]="{item}">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs"
                  v-on="on">{{item.as_model_id}}</span>
          </template>
          <span><span class="error--text font-weight-bold">AS Model ID: </span> Gene _ Chromsome _ Strand _ AS Type _ AS Model Coordinates</span>
        </v-tooltip>
      </template>
      <!-- item.gene_name slot -->
      <template v-slot:[`item.gene_name`]="{item}">
        <a @click="goToGeneSearchResult(item.gene_name)">
          <span class="font-weight-bold">{{item.gene_name}}</span>
        </a>
      </template>
      <!-- header.pct_samples slot -->
      <template v-slot:[`header.pct_samples`]="{header}">
        {{header.text}}
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small
                    v-bind="attrs"
                    v-on="on">mdi-help-circle</v-icon>
          </template>
          <span>Percent of samples with PSI values</span>
        </v-tooltip>
      </template>
      <!-- header.dpsi slot -->
      <template v-slot:[`header.dpsi`]="{header}">
        {{header.text}}
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small
                    v-bind="attrs"
                    v-on="on">mdi-help-circle</v-icon>
          </template>
          <span>Delta PSI = PSI Tumor - PSI Normal</span>
        </v-tooltip>
      </template>
      <!-- header.pvalue slot -->
      <template v-slot:[`header.pvalue`]="{header}">
        {{header.text}}
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small
                    v-bind="attrs"
                    v-on="on">mdi-help-circle</v-icon>
          </template>
          <span>p value in Wilcoxon test</span>
        </v-tooltip>
      </template>
      <!-- header.fdr slot -->
      <template v-slot:[`header.fdr`]="{header}">
        {{header.text}}
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small
                    v-bind="attrs"
                    v-on="on">mdi-help-circle</v-icon>
          </template>
          <span>FDR value in Wilcoxon test</span>
        </v-tooltip>
      </template>

      <!-- header.is_dase slot -->
      <template v-slot:[`header.is_dase`]="{header}">
        {{header.text}}
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small
                    v-bind="attrs"
                    v-on="on">mdi-help-circle</v-icon>
          </template>
          <span>Differential Alternative Splicing Events</span><br>
          <span>Filter criteria: |Delta PSI| > 0.1 & FDR < 0.05</span>
        </v-tooltip>
      </template>

      <!-- header.regulatory_explorer slot -->
      <template v-slot:[`header.regulatory_explorer`]="{header}">
        {{header.text}}
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small
                    v-bind="attrs"
                    v-on="on">mdi-help-circle</v-icon>
          </template>
          <span>Splicing Regulatory Explorer</span>
        </v-tooltip>
      </template>

      <!-- header.survival slot -->
      <template v-slot:[`header.survival`]="{header}">
        {{header.text}}
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small
                    v-bind="attrs"
                    v-on="on">mdi-help-circle</v-icon>
          </template>
          <span>Survival Analysis</span>
        </v-tooltip>
      </template>

      <!-- header.staging slot -->
      <template v-slot:[`header.staging`]="{header}">
        {{header.text}}
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small
                    v-bind="attrs"
                    v-on="on">mdi-help-circle</v-icon>
          </template>
          <span>Cancer Staging Analysis</span>
        </v-tooltip>
      </template>

      <!-- item.visualization slot -->
      <template v-slot:[`item.visualization`]="{item}">
        <v-btn @click="goToVisualiztion(item.gene_name, item.as_type, item.event_coordinates)"
               color="info"
               icon
               :disabled="item.as_type == 'Complex'">
          <v-icon>mdi-tune</v-icon>
        </v-btn>
      </template>

      <!-- item.regulatory_explorer slot -->
      <template v-slot:[`item.regulatory_explorer`]="{item}">
        <v-btn @click="goToRegulationExplorer(item.gene_name, 'TCGA', item.project_id, item.event_id)"
               color="grey"
               icon
               :disabled="item.source != 'TCGA' || item.as_type == 'Complex' || item.pct_samples <= 0.3 || item.tumor_psi <= 0.1 || item.tumor_psi == 1">
          <svg class="icon"
               aria-hidden="true"
               v-if="item.source != 'TCGA' || item.as_type == 'Complex' || item.pct_samples <= 0.3 || item.tumor_psi <= 0.1 || item.tumor_psi == 1">
            <use xlink:href="#icon-GridView-diable"></use>
          </svg>
          <svg class="icon"
               aria-hidden="true"
               v-else>
            <use xlink:href="#icon-GridView-oncoprint"></use>
          </svg>
        </v-btn>
      </template>

      <!-- item.survival slot -->
      <template v-slot:[`item.survival`]="{item}">
        <v-btn @click="goToSurvival(item.gene_name, 'TCGA', item.project_id, item.event_id)"
               color="info"
               icon
               :disabled="item.source != 'TCGA' || item.as_type == 'Complex' || !survival_project_items.includes(item.project_id) || item.pct_samples <= 0.3 || item.tumor_psi <= 0.1 || item.tumor_psi == 1">
          <svg class="icon"
               aria-hidden="true"
               v-if="item.source != 'TCGA' || item.as_type == 'Complex' || !survival_project_items.includes(item.project_id) || item.pct_samples <= 0.3 || item.tumor_psi <= 0.1 || item.tumor_psi == 1">
            <use xlink:href="#icon-linechart-diable"></use>
          </svg>
          <svg class="icon"
               aria-hidden="true"
               v-else>
            <use xlink:href="#icon-linechart"></use>
          </svg>
        </v-btn>
      </template>

      <!-- item.staging slot -->
      <template v-slot:[`item.staging`]="{item}">
        <v-btn @click="goToStaging(item.gene_name, 'TCGA', item.project_id, item.event_id)"
               color="info"
               icon
               :disabled="item.source != 'TCGA' || item.as_type == 'Complex' || !stage_project_items.includes(item.project_id) || item.pct_samples <= 0.3 || item.tumor_psi <= 0.1 || item.tumor_psi == 1">
          <svg class="icon"
               aria-hidden="true"
               v-if="item.source != 'TCGA' || item.as_type == 'Complex' || !stage_project_items.includes(item.project_id) || item.pct_samples <= 0.3 || item.tumor_psi <= 0.1 || item.tumor_psi == 1">
            <use xlink:href="#icon-xiangxiantu-disable"></use>
          </svg>
          <svg class="icon"
               aria-hidden="true"
               v-else>
            <use xlink:href="#icon-xiangxiantu"></use>
          </svg>
        </v-btn>
      </template>

      <!-- header.data-table-select slot-->
      <template v-slot:[`header.data-table-select`]="{ on, props }">
        <v-simple-checkbox color="success"
                           v-bind="props"
                           v-on="on"></v-simple-checkbox>
      </template>

      <!-- item.data-table-select slot -->
      <template v-slot:[`item.data-table-select`]="{ isSelected, select }">
        <v-simple-checkbox color="info"
                           :value="isSelected"
                           @input="select($event)"></v-simple-checkbox>
      </template>
    </v-data-table>

    <div class="d-flex justify-center mt-1 mb-n2"
         style="max-height:50px">
      <span class="text-body-1 mt-3">Items per page</span>
      <v-select v-model="pageSize"
                dense
                outlined
                :items="pageSizes"
                class="pagesize-select mt-1 mx-2"
                @input="handlePageSizeChange(pageSize)">
      </v-select>
      <span class="text-body-1 mt-3 mx-2">Total <span class="font-weight-bold info--text">{{totalItems}}</span>
        items </span>
      <v-pagination v-model="currentPage"
                    color="info"
                    :length="totalPages"
                    :total-visible="5"
                    class="mx-1"
                    @input="handlePageChange"
                    prev-icon="mdi-menu-left"
                    next-icon="mdi-menu-right">
      </v-pagination>
      <span class="text-body-1 mt-2 mx-2">Page</span>
      <v-text-field outlined
                    dense
                    v-model="whichPage"
                    type="number"
                    class="jump-select"
                    append-icon="">
      </v-text-field>
      <v-btn color=""
             depressed
             outlined
             class="mx-2"
             @click="jumpPage()">Jump</v-btn>
    </div>
  </v-col>
</template>

<script>
import { getSpliceDatasets } from 'node/browse.js';
import JsonExcel from 'vue-json-excel';

export default {
  name: 'DatasetsResultByGene',
  components: {
    JsonExcel
  },
  data() {
    return {
      search: '',
      selected: [],
      singleSelect: false,
      loading: true,
      currentPage: 1,
      whichPage: 1,
      totalPages: 0,
      pageSize: 5,
      pageSizes: [5, 10, 20, 50, 100],
      totalItems: 0,
      selectedHeader: ['BioProject', 'Gene Name', 'Cancer Type', 'AS Type',
        'ORF', 'PSI Tumor', 'PSI Normal', 'Delta PSI', 'FDR',
        'DASE', 'Visualization', 'Explorer', 'Survival', 'Staging'],
      headers: [
        { text: 'Event ID', value: 'event_id', align: 'start', sortable: false, },
        { text: 'AS Model ID', value: 'as_model_id', align: 'start', sortable: false, },
        { text: 'Source', value: 'source', align: 'start', sortable: false, },
        { text: 'BioProject', value: 'project_id', align: 'start', sortable: false, },
        { text: 'Gene Name', value: 'gene_name', align: 'start', sortable: true, },
        { text: 'Chr', value: 'chr', align: 'start', sortable: true, },
        { text: 'Strand', value: 'strand', align: 'start', sortable: true, },
        { text: 'Cancer Type', value: 'cancer_name', align: 'start', sortable: true },
        { text: 'Primary Site', value: 'primary_site', align: 'start' },
        { text: 'AS Type', value: 'as_type', align: 'start' },
        { text: 'Event Coordinates', value: 'event_coordinates', align: 'start', },
        { text: 'Alt Coordinates', value: 'alt_coordinates', align: 'start', sortable: false, },
        { text: 'ORF', value: 'orf', align: 'start', sortable: false, },
        { text: 'Control', value: 'control', align: 'start', sortable: true, },
        { text: 'Control Size', value: 'control_size', align: 'start', sortable: false, },
        { text: 'Case', value: 'case', align: 'start', sortable: false, },
        { text: 'Case Size', value: 'case_size', align: 'start', sortable: false, },
        { text: 'Percent Samples', value: 'pct_samples', align: 'start', sortable: false, },
        { text: 'PSI Tumor', value: 'tumor_psi', align: 'start', sortable: false, },
        { text: 'PSI Normal', value: 'normal_psi', align: 'start', sortable: false, },
        { text: 'Delta PSI', value: 'dpsi', align: 'start', sortable: false, },
        { text: 'P Value', value: 'pvalue', align: 'start', sortable: false, },
        { text: 'FDR', value: 'fdr', align: 'start', sortable: false, },
        { text: 'DASE', value: 'is_dase', align: 'start', sortable: false, },
        { text: 'Visualization', value: 'visualization', align: 'center', sortable: false, },
        { text: 'Explorer', value: 'regulatory_explorer', align: 'center', sortable: false, },
        { text: 'Survival', value: 'survival', align: 'center', sortable: false, },
        { text: 'Staging', value: 'staging', align: 'center', sortable: false, },
      ],

      gene_select: null,
      splice_datasets: [],

      source_select: null,
      project_select: null,

      survival_project_items: ["TCGA-ACC", "TCGA-BLCA", "TCGA-BRCA", "TCGA-CESC", "TCGA-CHOL",
        "TCGA-COAD", "TCGA-DLBC", "TCGA-ESCA", "TCGA-GBM", "TCGA-HNSC", "TCGA-KICH",
        "TCGA-KIRC", "TCGA-KIRP", "TCGA-LAML", "TCGA-LGG", "TCGA-LUAD", "TCGA-LUSC", "TCGA-MESO",
        "TCGA-OV", "TCGA-PAAD", "TCGA-PCPG", "TCGA-PRAD", "TCGA-READ", "TCGA-SARC",
        "TCGA-SKCM", "TCGA-STAD", "TCGA-THCA", "TCGA-THYM", "TCGA-UCEC", "TCGA-UCS", "TCGA-UVM",
      ],
      stage_project_items: ["TCGA-ACC", "TCGA-BLCA", "TCGA-BRCA", "TCGA-CHOL",
        "TCGA-COAD", "TCGA-ESCA", "TCGA-HNSC", "TCGA-KICH", "TCGA-KIRC", "TCGA-KIRP",
        "TCGA-LUAD", "TCGA-LUSC", "TCGA-MESO", "TCGA-PAAD", "TCGA-READ", "TCGA-SKCM",
        "TCGA-STAD", "TCGA-THCA",
      ],
    }
  },
  props: {
    geneName: {
      type: String,
      default() {
        return
      }
    },
    sourceName: {
      type: String,
      default() {
        return
      }
    },
    projectId: {
      type: String,
      default() {
        return
      }
    }
  },
  created() {
  },
  watch: {
    splice_datasets(val) {
      if (val) {
        this.loading = false;
      }
    },
    geneName(newVal, oldVal) {
      // console.log(newVal);
      this.gene_select = newVal;
      // this.getSpliceDatasets();
    },
    sourceName(newVal, oldVal) {
      // console.log(newVal);
      this.source_select = newVal;
      //   this.getSpliceDatasets();
    },
    projectId(newVal, oldVal) {
      // console.log(newVal);
      this.project_select = newVal;
      //   this.getSpliceDatasets();
    },
    listenMultiData(val) {
      if (val.gene_select && val.source_select && val.project_select) {
        this.getSpliceDatasets();
      } else if (val.gene_select) {
        this.getSpliceDatasets();
      }
    }
  },
  computed: {
    selectedHeaders() {
      return this.headers.filter(h => this.selectedHeader.includes(h.text))
    },
    // watch multi data props meanwhile.
    listenMultiData() {
      const { gene_select, source_select, project_select } = this;
      return {
        gene_select, source_select, project_select
      }
    }
  },
  methods: {
    getRequestParams(genename, source, project, page, pageSize) {
      let params = {};

      if (genename) {
        params["genename"] = genename;
      }

      if (source) {
        params["source"] = source;
      }

      if (project) {
        params["project"] = project;
      }

      if (page) {
        params["page"] = page - 1;
      }

      if (pageSize) {
        params["size"] = pageSize;
      }

      return params;
    },
    async getSpliceDatasets() {
      this.loading = true;
      const params = this.getRequestParams(
        this.gene_select,
        this.source_select,
        this.project_select,
        this.currentPage,
        this.pageSize
      );
      await getSpliceDatasets(params).then(
        data => {
          if (data) {
            // console.log(data);
            this.loading = false;
            this.totalItems = data.totalItems;
            this.splice_datasets = data.items;
            this.currentPage = data.currentPage;
            this.totalPages = data.totalPages;
          }
        }
      ).catch(err => console.error(err))
    },
    handlePageChange(value) {
      this.currentPage = value;
      this.whichPage = this.currentPage;
      this.getSpliceDatasets();
    },
    handlePageSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.getSpliceDatasets();
    },
    jumpPage() {
      // console.log(+this.whichPage);
      if (+this.whichPage < 1) {
        this.whichPage = 1;
      }
      if (+this.whichPage > this.totalPages) {
        this.whichPage = this.totalPages;
      }
      this.currentPage = +this.whichPage;
      this.getSpliceDatasets();
    },
    goToGeneSearchResult(genename) {
      if (genename) {
        this.$router.push({
          path: '/search',
          query: {
            genename: genename,
          }
        }).catch(err => {
          if (
            err.name !== 'NavigationDuplicated' &&
            !err.message.includes('Avoided redundant navigation to current location')
          ) {
            // But print any other errors to the console
            logError(err);
          }
        })
      } else if (genename == null || genename == '') {
        window.alert("Sorry, please input the gene name !")
      }
    },
    goToVisualiztion(genename, astype, coordinate) {
      this.$router.push({
        path: '/splicevis',
        query: {
          genename: genename,
          astype: astype,
          coordinate: coordinate
        }
      }).catch(err => {
        if (
          err.name !== 'NavigationDuplicated' &&
          !err.message.includes('Avoided redundant navigation to current location')
        ) {
          // But print any other errors to the console
          logError(err);
        }
      })
    },
    goToRegulationExplorer(genename, source, project, eventid) {
      if (genename && source && project != "-") {
        this.$router.push({
          path: '/tools/regexp',
          query: {
            genename: genename,
            source: source,
            project: project,
            eventid: eventid
          }
        }).catch(err => {
          if (
            err.name !== 'NavigationDuplicated' &&
            !err.message.includes('Avoided redundant navigation to current location')
          ) {
            // But print any other errors to the console
            logError(err);
          }
        })
      }
    },
    goToSurvival(genename, source, project, eventid) {
      if (genename && source && project != "-") {
        this.$router.push({
          path: '/tools/survival/asevent',
          query: {
            genename: genename,
            source: source,
            project: project,
            eventid: eventid
          }
        }).catch(err => {
          if (
            err.name !== 'NavigationDuplicated' &&
            !err.message.includes('Avoided redundant navigation to current location')
          ) {
            // But print any other errors to the console
            logError(err);
          }
        })
      }
    },
    goToStaging(genename, source, project, eventid) {
      if (genename && source && project != "-") {
        this.$router.push({
          path: '/tools/stage/asevent',
          query: {
            genename: genename,
            source: source,
            project: project,
            eventid: eventid
          }
        }).catch(err => {
          if (
            err.name !== 'NavigationDuplicated' &&
            !err.message.includes('Avoided redundant navigation to current location')
          ) {
            // But print any other errors to the console
            logError(err);
          }
        })
      }
    },
    showAll() {
      this.headers.map(h => {
        this.selectedHeader.push(h.text)
      })
    },
    showNone() {
      this.selectedHeader = [];
    },
    showDefault() {
      this.selectedHeader = ['BioProject', 'Gene Name', 'Cancer Type', 'AS Type',
        'ORF', 'PSI Tumor', 'PSI Normal', 'Delta PSI', 'FDR',
        'DASE', 'Visualization', 'Explorer', 'Survival', 'Staging'];
    },
  },
}
</script>
<style scoped lang="sass">
a
  text-decoration: none
  color: #2196F3

a:hover
  text-decoration: underline

.v-btn
  text-transform: none
  letter-spacing: 0em
  font-size: 0.875rem

// .v-sheet.v-card
//   border-radius: 15px

.v-card__title
  line-height: 0.5rem

.v-data-table ::v-deep th
  font-size: 0.87rem !important
.v-data-table ::v-deep td
  font-size: 0.875rem !important

.data-table-style
  border: thin solid rgba(0, 0, 0, 0.12)

.v-item-group
  flex: 0 1 auto
  position: relative
  display: inline-flex
  flex-wrap: wrap
  max-width: 100%
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)

.pagesize-select
  max-width: 9%

.jump-select
  max-width: 8%

.v-input--selection-controls
  margin-top: 2px
  padding-top: 0px
</style>